import { useEffect, useRef, useState } from 'react'
import { ChevronUp, ChevronDown, X } from 'lucide-react'
import type { SearchAddon } from '@xterm/addon-search'

interface TerminalSearchBarProps {
  searchAddon: SearchAddon
  onClose: () => void
}

const SEARCH_DECORATIONS = {
  matchBackground: '#515c6a',
  matchBorder: '#515c6a',
  matchOverviewRuler: '#515c6a',
  activeMatchBackground: '#c9a834',
  activeMatchBorder: '#c9a834',
  activeMatchColorOverviewRuler: '#c9a834',
}

export default function TerminalSearchBar({ searchAddon, onClose }: TerminalSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [resultIndex, setResultIndex] = useState(-1)
  const [resultCount, setResultCount] = useState(0)

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Listen for result changes
  useEffect(() => {
    const disposable = searchAddon.onDidChangeResults((e) => {
      setResultIndex(e.resultIndex)
      setResultCount(e.resultCount)
    })
    return () => disposable.dispose()
  }, [searchAddon])

  // Clear decorations on unmount
  useEffect(() => {
    return () => {
      searchAddon.clearDecorations()
    }
  }, [searchAddon])

  const handleInputChange = (value: string) => {
    setQuery(value)
    if (value) {
      searchAddon.findNext(value, { incremental: true, decorations: SEARCH_DECORATIONS })
    } else {
      searchAddon.clearDecorations()
      setResultIndex(-1)
      setResultCount(0)
    }
  }

  const handleFindNext = () => {
    if (query) {
      searchAddon.findNext(query, { decorations: SEARCH_DECORATIONS })
    }
  }

  const handleFindPrevious = () => {
    if (query) {
      searchAddon.findPrevious(query, { decorations: SEARCH_DECORATIONS })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) {
        handleFindPrevious()
      } else {
        handleFindNext()
      }
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  const matchInfo =
    query && resultCount > 0
      ? resultIndex >= 0
        ? `${resultIndex + 1}/${resultCount}`
        : `${resultCount}+`
      : query
        ? 'No results'
        : null

  return (
    <div className="absolute top-2 right-2 z-20 bg-card border border-border rounded-lg shadow-lg p-2 flex items-center gap-2">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search..."
        className="w-48 sm:w-64 px-2 py-1 text-sm bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {matchInfo && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">{matchInfo}</span>
      )}
      <button
        onClick={handleFindPrevious}
        disabled={!query || resultCount === 0}
        className="p-1 rounded hover:bg-secondary disabled:opacity-30 transition-colors"
        title="Previous match (Shift+Enter)"
      >
        <ChevronUp className="w-4 h-4" />
      </button>
      <button
        onClick={handleFindNext}
        disabled={!query || resultCount === 0}
        className="p-1 rounded hover:bg-secondary disabled:opacity-30 transition-colors"
        title="Next match (Enter)"
      >
        <ChevronDown className="w-4 h-4" />
      </button>
      <button
        onClick={onClose}
        className="p-1 rounded hover:bg-secondary transition-colors"
        title="Close (Escape)"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
